import i18n from "@/i18n";

export type RecurrenceFrequency = "weekly" | "monthly" | "yearly";

export interface RecurrenceRuleInput {
  frequency: RecurrenceFrequency;
  interval: number;
  weekdays?: number[];
  monthday?: number | null;
  endsOn?: Date | null;
  maxOccurrences?: number | null;
  windowDays?: number;
}

export interface RecurrenceOccurrence {
  startsAt: Date;
  endsAt: Date;
  occurrenceDate: string;
}

const t = (key: string, options?: { count?: number; unit?: string }) =>
  i18n.t(key, { ns: "calendar", ...options });

const DAY_MS = 24 * 60 * 60 * 1000;

const clampInterval = (value: number) => {
  if (!Number.isFinite(value) || value < 1) return 1;
  return Math.floor(value);
};

const normalizeWeekdays = (values: number[] | undefined, fallback: number): number[] => {
  const source = Array.isArray(values) && values.length > 0 ? values : [fallback];
  const normalized = Array.from(
    new Set(
      source
        .filter((value) => Number.isFinite(value))
        .map((value) => Math.max(0, Math.min(6, Math.floor(value)))),
    ),
  );
  return normalized.length > 0 ? normalized.sort((a, b) => a - b) : [fallback];
};

const toDateOnlyIso = (date: Date): string => {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${year}-${month}-${day}`;
};

const startOfLocalDay = (date: Date): Date => {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
};

const startOfWeekMonday = (date: Date): Date => {
  const start = startOfLocalDay(date);
  const mondayBasedOffset = (start.getDay() + 6) % 7;
  return addDays(start, -mondayBasedOffset);
};

const addDays = (date: Date, days: number): Date => {
  return new Date(date.getTime() + days * DAY_MS);
};

const weekDiff = (from: Date, to: Date): number => {
  const days = Math.floor((startOfLocalDay(to).getTime() - startOfLocalDay(from).getTime()) / DAY_MS);
  return Math.floor(days / 7);
};

export const formatRecurrenceSummary = (rule: {
  frequency: RecurrenceFrequency;
  interval: number;
  endsOn?: string | Date | null;
}) => {
  const freqText =
    rule.frequency === "weekly"
      ? t("phase8.recurrence.summary.frequency.weekly")
      : rule.frequency === "monthly"
        ? t("phase8.recurrence.summary.frequency.monthly")
        : t("phase8.recurrence.summary.frequency.yearly");
  const interval = clampInterval(rule.interval);
  const intervalUnit =
    rule.frequency === "weekly"
        ? t("phase8.recurrence.summary.units.weeks")
        : rule.frequency === "monthly"
        ? t("phase8.recurrence.summary.units.months")
        : t("phase8.recurrence.summary.units.years");
  const intervalText =
    interval > 1 ? t("phase8.recurrence.summary.every", { count: interval, unit: intervalUnit }) : freqText;
  const parseDateOnlyString = (value: string): Date | null => {
    const [year, month, day] = value.split("-").map((part) => Number(part));
    if (!year || !month || !day) return null;
    return new Date(year, month - 1, day, 0, 0, 0, 0);
  };
  const endDate =
    typeof rule.endsOn === "string"
      ? parseDateOnlyString(rule.endsOn)
      : rule.endsOn instanceof Date
        ? rule.endsOn
        : null;
  const base = t("phase8.recurrence.summary.base");
  if (!endDate || Number.isNaN(endDate.getTime())) return `${base} ${intervalText}`;
  const locale = i18n.language || "pt-BR";
  const until = t("phase8.recurrence.summary.until");
  return `${base} ${intervalText} ${until} ${new Intl.DateTimeFormat(locale, {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  }).format(endDate)}`;
};

export function generateRecurrenceOccurrences(
  startsAt: Date,
  endsAt: Date,
  rule: RecurrenceRuleInput,
): RecurrenceOccurrence[] {
  const durationMs = endsAt.getTime() - startsAt.getTime();
  if (!Number.isFinite(durationMs) || durationMs <= 0) return [];

  const interval = clampInterval(rule.interval);
  const windowDays = Number.isFinite(rule.windowDays) ? Math.max(1, Math.floor(rule.windowDays as number)) : 90;
  const maxByCount = rule.maxOccurrences && rule.maxOccurrences > 0 ? Math.floor(rule.maxOccurrences) : Number.POSITIVE_INFINITY;
  const hardEnd = rule.endsOn ? startOfLocalDay(rule.endsOn) : null;
  const windowEnd = addDays(startOfLocalDay(startsAt), windowDays);
  const finalDate = hardEnd ? (hardEnd < windowEnd ? hardEnd : windowEnd) : windowEnd;

  const baseDay = startsAt.getDay();
  const weekdays = normalizeWeekdays(rule.weekdays, baseDay);

  const occurrences: RecurrenceOccurrence[] = [];

  if (rule.frequency === "weekly") {
    if (startOfLocalDay(startsAt) <= finalDate && occurrences.length < maxByCount) {
      occurrences.push({
        startsAt: new Date(startsAt),
        endsAt: new Date(startsAt.getTime() + durationMs),
        occurrenceDate: toDateOnlyIso(startsAt),
      });
    }

    const startDay = startsAt.getDay();
    const startPosInWeek = (startDay + 6) % 7;
    const weekdayPositions = weekdays.map((day) => (day + 6) % 7);
    const hasCurrentOrFutureDay = weekdayPositions.some((pos) => pos >= startPosInWeek);
    const hasEarlierDay = weekdayPositions.some((pos) => pos < startPosInWeek);
    const hasPartialWeekSelection = interval === 1 && hasCurrentOrFutureDay && hasEarlierDay;

    const baseWeekStart = startOfWeekMonday(startsAt);
    const intervalWeekStart = hasPartialWeekSelection ? addDays(baseWeekStart, 7) : baseWeekStart;
    const firstAllowedDate = hasPartialWeekSelection ? intervalWeekStart : startOfLocalDay(startsAt);
    const firstFutureDate = addDays(startOfLocalDay(startsAt), 1);

    let cursor = firstAllowedDate > firstFutureDate ? firstAllowedDate : firstFutureDate;
    let guard = 0;
    while (cursor <= finalDate && occurrences.length < maxByCount && guard < 2000) {
      guard += 1;
      const day = cursor.getDay();
      const wDiff = weekDiff(intervalWeekStart, cursor);
      const respectsInterval = wDiff >= 0 && wDiff % interval === 0;
      if (respectsInterval && weekdays.includes(day)) {
        const occurrenceStart = new Date(cursor);
        occurrenceStart.setHours(startsAt.getHours(), startsAt.getMinutes(), startsAt.getSeconds(), startsAt.getMilliseconds());
        if (startOfLocalDay(occurrenceStart) >= firstAllowedDate && occurrenceStart > startsAt) {
          const occurrenceEnd = new Date(occurrenceStart.getTime() + durationMs);
          occurrences.push({
            startsAt: occurrenceStart,
            endsAt: occurrenceEnd,
            occurrenceDate: toDateOnlyIso(occurrenceStart),
          });
        }
      }
      cursor = addDays(cursor, 1);
    }
    return occurrences;
  }

  if (rule.frequency === "yearly") {
    let yearCursor = startsAt.getFullYear();
    let guard = 0;
    while (occurrences.length < maxByCount && guard < 500) {
      guard += 1;
      const yearDiff = yearCursor - startsAt.getFullYear();
      if (yearDiff >= 0 && yearDiff % interval === 0) {
        const lastDayOfMonth = new Date(yearCursor, startsAt.getMonth() + 1, 0).getDate();
        const dayOfMonth = Math.min(startsAt.getDate(), lastDayOfMonth);
        const occurrenceStart = new Date(
          yearCursor,
          startsAt.getMonth(),
          dayOfMonth,
          startsAt.getHours(),
          startsAt.getMinutes(),
          startsAt.getSeconds(),
          startsAt.getMilliseconds(),
        );
        if (!Number.isNaN(occurrenceStart.getTime()) && occurrenceStart >= startsAt && startOfLocalDay(occurrenceStart) <= finalDate) {
          const occurrenceEnd = new Date(occurrenceStart.getTime() + durationMs);
          occurrences.push({
            startsAt: occurrenceStart,
            endsAt: occurrenceEnd,
            occurrenceDate: toDateOnlyIso(occurrenceStart),
          });
        }
      }

      yearCursor += 1;
      const nextYearLastDay = new Date(yearCursor, startsAt.getMonth() + 1, 0).getDate();
      const nextYearDay = Math.min(startsAt.getDate(), nextYearLastDay);
      if (startOfLocalDay(new Date(yearCursor, startsAt.getMonth(), nextYearDay)) > finalDate) break;
    }
    return occurrences;
  }

  // monthly
  const targetDay = rule.monthday && rule.monthday > 0 ? Math.min(31, Math.floor(rule.monthday)) : startsAt.getDate();
  let monthCursor = new Date(startsAt.getFullYear(), startsAt.getMonth(), 1);
  let guard = 0;
  while (occurrences.length < maxByCount && guard < 500) {
    guard += 1;
    const year = monthCursor.getFullYear();
    const month = monthCursor.getMonth();
    const monthIndexDiff = (year - startsAt.getFullYear()) * 12 + (month - startsAt.getMonth());
    if (monthIndexDiff >= 0 && monthIndexDiff % interval === 0) {
      const lastDay = new Date(year, month + 1, 0).getDate();
      const day = Math.min(targetDay, lastDay);
      const occurrenceStart = new Date(year, month, day, startsAt.getHours(), startsAt.getMinutes(), startsAt.getSeconds(), startsAt.getMilliseconds());
      if (occurrenceStart >= startsAt && startOfLocalDay(occurrenceStart) <= finalDate) {
        const occurrenceEnd = new Date(occurrenceStart.getTime() + durationMs);
        occurrences.push({
          startsAt: occurrenceStart,
          endsAt: occurrenceEnd,
          occurrenceDate: toDateOnlyIso(occurrenceStart),
        });
      }
    }

    monthCursor = new Date(year, month + 1, 1);
    if (startOfLocalDay(monthCursor) > finalDate) break;
  }

  return occurrences;
}
